import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import Modal from 'Views/Components/Modal/Modal';
import { Button, Colors, Display } from 'Views/Components/Button/Button';

import { GET_COURSE_DETAILS_QUERY } from 'Queries/GraphQlQueries/Course/CourseQueries';

import ChipGroup from 'Views/Components/Chip/ChipGroup';
import Chip from 'Views/Components/Chip/Chip';
import Skeleton from 'Views/Components/Skeleton/Skeleton';

import { indexTermsByStudyPeriods } from 'Util/CourseUtils';
import { CourseEntity, ICourseEntityAttributes, TermEntity } from 'Models/Entities';
import { capitalize, toLower } from 'lodash';

interface CourseInfoModalProps {
	isOpen: boolean,
	courseId: string,
	onCloseModal: () => void
}

const CourseInfoModal = (props: CourseInfoModalProps) => {
	const {
		isOpen,
		courseId,
		onCloseModal,
	} = props;

	const { loading, data } = useQuery<{
		courseEntity: ICourseEntityAttributes
	}>(
		GET_COURSE_DETAILS_QUERY,
		{
			fetchPolicy: 'network-only',
			variables: {
				id: courseId,
			},
		},
	);

	const course = useMemo(() => new CourseEntity(data?.courseEntity), [data]);

	const campusNames = useMemo(
		() => course?.campuss.map(c => c.campus.name),
		[course],
	);

	const graduateLevels = useMemo(
		() => course?.graduateLevels.map(level => level.graduateLevel.name),
		[course],
	);

	const studyPeriods = useMemo(
		() => course?.getStudyPeriods(),
		[course],
	);

	const termsByPeriodId: Record<string, TermEntity[]> = useMemo(
		() => course ? indexTermsByStudyPeriods(course.terms.map(courseTerm => courseTerm.term)) : {},
		[course],
	);

	const discipline = course?.discipline;
	const school = discipline?.school;
	const faculty = school?.faculty;

	const requisiteLabel = useMemo(() => {
		const topLevelLogicGroup = course?.requisiteLogicGroups
			.find(logicGroup => logicGroup.isTopLevelGroup);

		return topLevelLogicGroup?.description && topLevelLogicGroup?.description.length
			? topLevelLogicGroup?.description
			: 'Nil';
	}, [course?.requisiteLogicGroups]);

	return (
		<Modal
			className="course-info-modal information-modal"
			label={`Course info modal ${courseId}`}
			isOpen={isOpen}
			onRequestClose={onCloseModal}
		>
			<div className="modal__header">
				{loading ? (
					<h4>Loading...</h4>
				) : (
					<h4><span className="course-code">{course?.code}</span>{` ${course?.name}`}</h4>
				)}
			</div>
			{loading ? (
				<div className="modal__message">
					<Skeleton lines={10} />
				</div>
			) : (
				<div className="modal__message">
					<div className="information-modal__section">
						<h6>{capitalize(window.lexicon.requisites)}</h6>
						<p>{requisiteLabel}</p>
					</div>
					<div className="information-modal__section-group-wrapper">
						<div className="information-modal__section-group">
							<div className="information-modal__section">
								<h6>{capitalize(window.lexicon.units)}</h6>
								<p>{course?.units}</p>
							</div>
							<div className="information-modal__section">
								<h6>Year</h6>
								<p>{course?.level}</p>
							</div>
							<div className="information-modal__section">
								<h6>Level</h6>
								<p>{course?.courseLevel.level}</p>
							</div>
							<div className="information-modal__section">
								<h6>{capitalize(window.lexicon.studyPeriod)} & {capitalize(window.lexicon.term)}</h6>
								{
									studyPeriods?.map(studyPeriod => {
										const termsForPeriod = termsByPeriodId[studyPeriod.id] || [];

										if (!studyPeriod) {
											return null;
										}

										return (
											<div
												key={`period-container-${studyPeriod.id}`}
												className="study-period-container"
											>
												<div className="period-item">
													<Chip key={studyPeriod.id} chipLabel={studyPeriod.name} />
												</div>
												<div>
													<ChipGroup verticalList>
														{termsForPeriod.length === 0 ? (
															<Chip key="term-none" chipLabel="-" />
														) : termsForPeriod.map(term => (
															<Chip key={term.id} chipLabel={term.name} />
														))}
													</ChipGroup>
												</div>
											</div>
										);
									})
								}
							</div>
						</div>
						<div className="information-modal__section-group">
							<div className="information-modal__section">
								<h6>{capitalize(window.lexicon.location)}</h6>
								<ChipGroup>
									{campusNames?.length === 0 ? (
										<Chip key="campus-none" chipLabel="-" />
									) : campusNames?.map(c => (
										<Chip key={c} chipLabel={c} />
									))}
								</ChipGroup>
							</div>
							<div className="information-modal__section">
								<h6>Graduate level</h6>
								<ChipGroup>
									{graduateLevels?.length === 0 ? (
										<Chip key="graduate-level-none" chipLabel="-" />
									) : graduateLevels?.map(l => (
										<Chip key={l} chipLabel={l} />
									))}
								</ChipGroup>
							</div>
							<div className="information-modal__section">
								<h6>{capitalize(window.lexicon.faculty)}</h6>
								<ChipGroup>
									<Chip chipLabel={!faculty ? '-' : faculty.name} />
								</ChipGroup>
							</div>
							<div className="information-modal__section">
								<h6>{capitalize(window.lexicon.school)}</h6>
								<ChipGroup>
									<Chip chipLabel={!school ? '-' : school.name} />
								</ChipGroup>
							</div>
							<div className="information-modal__section">
								<h6>{capitalize(window.lexicon.discipline)}</h6>
								<ChipGroup>
									<Chip chipLabel={!discipline ? '-' : discipline.name} />
								</ChipGroup>
							</div>
						</div>
					</div>
					<div className="information-modal__section">
						<h6>Description</h6>
						<p>{course?.description}</p>
					</div>
				</div>
			)}
			<div className="modal__actions">
				<Button
					key="cancel"
					onClick={onCloseModal}
					type="button"
					colors={Colors.Primary}
					display={Display.Outline}
				>
					Close
				</Button>
			</div>
		</Modal>
	);
};

export default CourseInfoModal;
